import type { AdapterAnswerCondition, AdapterApplyAnswers, AdapterQuestion } from "./types";

/**
 * Answer keys are dotted paths: `entities.position` is the `position` field of the `entities`
 * object. A dot always separates path segments, so a literal dotted key cannot be expressed.
 */

const FORBIDDEN_SEGMENTS = new Set(["__proto__", "constructor", "prototype"]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const toSegments = (path: string): string[] => {
  const segments = path.split(".");
  if (segments.some((segment) => segment === "" || FORBIDDEN_SEGMENTS.has(segment))) {
    throw new Error(`"${path}" is not a valid answer path.`);
  }
  return segments;
};

export const readAnswer = (answers: AdapterApplyAnswers | Record<string, unknown>, path: string): unknown => {
  let current: unknown = answers;
  for (const segment of toSegments(path)) {
    if (!isRecord(current)) return undefined;
    current = current[segment];
  }
  return current;
};

/**
 * `text` with each `{{answer.path}}` replaced by that answer. An unanswered one is blank, or kept as written with
 * `keepUnanswered` (for text that may show `{{...}}` for other reasons, such as template syntax in step content).
 */
export const fillAnswerTemplates = (
  text: string,
  answers: AdapterApplyAnswers | Record<string, unknown>,
  { keepUnanswered = false }: { keepUnanswered?: boolean } = {},
): string =>
  text.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (placeholder, path: string) => {
    let value: unknown;
    try {
      value = readAnswer(answers, path);
    } catch {
      // Not an answer path at all, e.g. `{{UnitType == Hour ? hours : days}}`.
      value = undefined;
    }
    return value === undefined || value === null || typeof value === "object"
      ? keepUnanswered ? placeholder : ""
      : String(value);
  });

/** Returns a copy of `answers` with `value` at `path`, leaving the input and its other branches untouched. */
export const writeAnswer = <T extends Record<string, unknown>>(answers: T, path: string, value: unknown): T => {
  const [head, ...rest] = toSegments(path);
  if (rest.length === 0) return { ...answers, [head]: value };
  const child = isRecord(answers[head]) ? answers[head] : {};
  return { ...answers, [head]: writeAnswer(child, rest.join("."), value) };
};

/** Returns a copy of `answers` without the given paths, dropping any object left empty by the removal. */
export const omitAnswers = <T extends Record<string, unknown>>(answers: T, paths: Iterable<string>): T => {
  const omit = (value: Record<string, unknown>, segments: string[]): Record<string, unknown> => {
    const [head, ...rest] = segments;
    if (!(head in value)) return value;
    const { [head]: child, ...others } = value;
    if (rest.length === 0) return others;
    if (!isRecord(child)) return value;
    const pruned = omit(child, rest);
    return Object.keys(pruned).length === 0 ? others : { ...others, [head]: pruned };
  };
  let result: Record<string, unknown> = answers;
  for (const path of paths) result = omit(result, toSegments(path));
  return result as T;
};

/** Later sources win; nested objects merge key by key so a partial saved branch keeps its default siblings. */
export const mergeAnswers = <T extends Record<string, unknown>>(...sources: Array<Partial<T> | undefined>): T => {
  const merge = (base: Record<string, unknown>, next: Record<string, unknown>): Record<string, unknown> => {
    const result = { ...base };
    for (const [key, value] of Object.entries(next)) {
      result[key] = isRecord(value) && isRecord(result[key]) ? merge(result[key] as Record<string, unknown>, value) : value;
    }
    return result;
  };
  return sources.reduce<Record<string, unknown>>((merged, source) => (source ? merge(merged, source) : merged), {}) as T;
};

/** Question paths where one is an ancestor of another, which would make their answers overwrite each other. */
export const getConflictingQuestionKeys = (questions: AdapterQuestion[]): Array<[string, string]> =>
  questions.flatMap((question, index) =>
    questions.slice(index + 1).flatMap((other): Array<[string, string]> =>
      question.key === other.key || question.key.startsWith(`${other.key}.`) || other.key.startsWith(`${question.key}.`)
        ? [[question.key, other.key]]
        : []),
  );

/** A condition value that matches any answer that is not blank, e.g. `showWhen: { "entities.workDay": ANSWER_IS_SET }`. */
export const ANSWER_IS_SET = Object.freeze({ $set: true as const });

const isSetCondition = (value: unknown): boolean => isRecord(value) && value.$set === true;

export const isAnswerSet = (value: unknown): boolean =>
  value !== undefined && value !== null && !(typeof value === "string" && value.trim() === "");

export const matchesAdapterAnswers = (
  answers: AdapterApplyAnswers,
  condition?: AdapterAnswerCondition,
): boolean =>
  !condition ||
  Object.entries(condition).every(([key, value]) =>
    isSetCondition(value) ? isAnswerSet(readAnswer(answers, key)) : readAnswer(answers, key) === value,
  );
